import React from 'react'
import { Link, Redirect, useParams } from 'react-router-dom'
import { Grid, Paper, makeStyles, useTheme, CircularProgress, Button } from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import { db } from '../../config/firebase'
import { addCampaignRegistration, removeGM } from '../../api/user'
import { updateDestinyPool } from '../../api/rolling'
import Roll from './Roll'
import Destiny from './Destiny'
import DicePool from './DicePool'
import Character from './Character'
import Info from './Info'
import Onboarding from './Onboarding'
import Skills from './Skills'
import Event from './Event'
import Notepad from './Notepad'
import NewSessionButton from '../../components/NewSessionButton'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    paddingTop: props => theme.spacing(props.isSmall ? 0 : 1),
    paddingBottom: props => theme.spacing(props.isSmall ? 0 : 1),
    paddingLeft: props => theme.spacing(props.isSmall ? 0 : 2),
    paddingRight: props => theme.spacing(props.isSmall ? 0 : 2)
  },
  paper: {
    marginTop: props => theme.spacing(props.isSmall ? 0 : 2),
    marginBottom: props => theme.spacing(props.isSmall ? 1 : 2),
    padding: props => theme.spacing(props.isSmall ? 1 : 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    borderRadius: props => props.isSmall && 0,
    opacity: props => props.disabled ? 0.5 : 1,
    pointerEvents: props => props.disabled ? 'none' : 'all'
  },
  sectionTitle: {
    textAlign: 'left',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    fontSize: 18
  }
}))

const Campaign = ({ user }) => {
  const { campaignId } = useParams()

  const [ campaignData, setCampaignData ] = React.useState({})
  const [ campaignLoading, setCampaignLoading ] = React.useState(true)
  const [ rollsLoading, setRollsLoading ] = React.useState(true)
  const [ rolls, setRolls ] = React.useState([])
  const [ characters, setCharacters ] = React.useState({})
  const [ charactersLoading, setCharactersLoading ] = React.useState(true)
  const [ events, setEvents ] = React.useState([])
  const [ eventsLoading, setEventsLoading ] = React.useState(true)
  const [ campaign404, setCampaign404 ] = React.useState(false)
  const [ shouldRedirect, setShouldRedirect ] = React.useState(false)
  const [ newCharModalIsOpen, setNewCharModalIsOpen ] = React.useState(false)
  const [ dicePool, setDicePool ] = React.useState({})
  const [ skillIndex, setSkillIndex ] = React.useState(false)
  const [ rollsOnly, setRollsOnly ] = React.useState(false)

  const destinyPool = campaignData.destinyPool
  const character = characters[user.uid]
  const isGM = campaignData.GM === user.uid
  const characterName = isGM ? 'GM' : character?.details?.name

  const theme = useTheme()
  const isDarkMode = theme.palette.type === 'dark'
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyles({ disabled: typeof character === 'undefined' && !isGM, isSmall })

  React.useEffect(() => {
    if (
      !campaignLoading && !charactersLoading && campaignData.GM !== user.uid
      &&
      !Object.prototype.hasOwnProperty.call(characters, user.uid)
    ) {
      setNewCharModalIsOpen(true)
    } else {
      setNewCharModalIsOpen(false)
    }
  }, [ campaignLoading, charactersLoading, characters, user, campaignData.GM ])

  React.useEffect(() => {
    let campaignUnsubscribe = () => {}
    let rollUnsubscribe = () => {}
    let charactersUnsubscribe = () => {}
    let eventsUnsubscribe = () => {}

    if (campaignId) {
      // Empty `set` just to set the `updated` field; then get campaign info.
      const campaignRef = db.collection('campaigns').doc(campaignId)

      campaignRef.get().then((doc) => {
        if (!doc.exists) {
          setCampaign404(true)
          setCampaignLoading(false)
          setTimeout(() => {
            setShouldRedirect(true)
          }, 5000)
        } else {
          campaignUnsubscribe = campaignRef.onSnapshot((querySnapshot) => {
            setCampaignData(querySnapshot.data())
            setCampaignLoading(false)
          })

          const rollRef = campaignRef.collection('rolls').orderBy('created', 'desc').limit(10)

          rollUnsubscribe = rollRef.onSnapshot((querySnapshot) => {
            setRollsLoading(false)
            const rolls = []
            querySnapshot.forEach(function(roll) {
              rolls.push({ ...roll.data(), id: roll.id })
            })
            setRolls(rolls)
          })

          const charactersRef = campaignRef.collection('characters').orderBy('created', 'desc').limit(10)

          charactersUnsubscribe = charactersRef.onSnapshot((querySnapshot) => {
            setCharactersLoading(false)
            const characters = {}
            querySnapshot.forEach(function(character) {
              characters[character.id] = character.data()
            })
            setCharacters(characters)
          })

          const eventsRef = campaignRef.collection('events').orderBy('created', 'desc').limit(10)

          eventsUnsubscribe = eventsRef.onSnapshot((querySnapshot) => {
            setEventsLoading(false)
            const events = []
            querySnapshot.forEach(function(event) {
              events.push({ ...event.data(), id: event.id })
            })
            setEvents(events)
          })
        }
      })
    } else {
      console.log('No campaignId is set!')
    }

    return () => {
      campaignUnsubscribe()
      rollUnsubscribe()
      charactersUnsubscribe()
      eventsUnsubscribe()
    }
  }, [ campaignId ])

  React.useEffect(() => {
    if (!campaignLoading && campaignData.campaignName) {
      addCampaignRegistration(user.uid, campaignId, campaignData.campaignName)
    }
  }, [ campaignLoading, campaignId, campaignData.campaignName, user.uid ])

  const onSetDestinyPool = async (action, key) => {
    try {
      const newPool = await updateDestinyPool(campaignId, characterName, action, key)
      return newPool
    } catch(e) {
      console.error(e)
    }
  }

  const onSetDicePool = async (pool, skillIndex) => {
    if (skillIndex !== false) setSkillIndex(skillIndex)
    setDicePool(pool)
  }

  const combinedEventList = React.useMemo(() => {
    let list = []
    if (!rollsOnly) list = [ ...rolls, ...events ]
    else list = rolls

    return list.sort((a, b) => {
      if (a.created > b.created) return -1
      else if (a.created < b.created) return 1
      else return 0
    })
  }, [ rolls, events, rollsOnly ])

  if (campaign404 || campaignLoading) {
    return (
      <div className={classes.root}>
        {shouldRedirect && <Redirect to='/' />}
        {campaignLoading ?
          <div style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 40
          }}>
            <CircularProgress color={isDarkMode ? 'secondary' : 'primary'} />
          </div>
          :
          <div css={`
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            height: 200px;
          `}>
            <div css={`margin-bottom: 8px;`}>That campaign does not exist!</div>
            <div>Redirecting to the <Link to='/'>home page</Link> in 5 seconds...</div>
          </div>
        }
      </div>
    )
  }
  return (
    <div className={classes.root}>
      <Onboarding titleClass={classes.sectionTitle} campaignData={campaignData} campaignId={campaignId} isOpen={newCharModalIsOpen} onClose={() => setNewCharModalIsOpen(false)} />
      <Grid container direction='row' justify='center' alignItems='flex-start' spacing={isSmall ? 0 : 3}>
        <Grid item xs={12} md={6}>
          {/* Destiny pool */}
          <Paper className={classes.paper} elevation={3} css={`min-height: 100px;`}>
            <Destiny
              onSetPool={onSetDestinyPool}
              loading={campaignLoading}
              pool={destinyPool}
              titleClass={classes.sectionTitle} />
          </Paper>

          {/* Dice area */}
          <Paper className={classes.paper} elevation={3}>
            <DicePool
              campaignData={campaignData}
              campaignId={campaignId}
              characterName={characterName}
              dicePool={dicePool}
              loading={rollsLoading}
              setDicePool={setDicePool}
              onSetDestinyPool={onSetDestinyPool}
              skillIndex={skillIndex}
              setSkillIndex={setSkillIndex} />
          </Paper>

          {/* Roll List */}
          <Paper className={classes.paper} elevation={3} css={`display: flex; flex-direction: column;`}>
            <div css={`display: flex; justify-content: space-between; align-items: center;`}>
              <p className={classes.sectionTitle}>
                Events
              </p>
              <div css={`display: flex; align-items: center; margin-bottom: 16px;`}>
                <input checked={rollsOnly} type='checkbox' css={`margin-right: 4px;`} onChange={() => setRollsOnly(!rollsOnly)} />
                Rolls only
              </div>
            </div>
            {(rollsLoading || eventsLoading) || combinedEventList.length === 0 ?
              <p css={`display: flex; flex: 1; justify-content: center; align-items: center; margin-bottom: 24px;`}>
                {(rollsLoading || eventsLoading) ? 'Loading...' : 'No rolls or events have been created yet'}
              </p>
              :
              <div css={`
                min-height: 190px;
                ${isSmall && 'max-height: 400px;'}
                overflow: auto;
              `}>
                <div>
                  {combinedEventList.map(({ created, description, event, displayName, id, roll, skillIndex }) => {
                    if (!created) return false

                    const sharedProps = {
                      campaignId,
                      created,
                      description,
                      displayName,
                      key: id,
                      id,
                      isDarkMode
                    }
                    return roll ?
                      <Roll {...sharedProps} roll={roll} skillIndex={skillIndex} />
                      :
                      <Event {...sharedProps} event={event} />
                  })}
                </div>
              </div>
            }
          </Paper>
        </Grid>

        {/* Right grid */}
        <Grid item xs={12} md={6}>
          {/* Campaign Info */}
          <Paper className={classes.paper} elevation={3}>
            <Info
              loading={campaignLoading || charactersLoading}
              campaignId={campaignId}
              campaignData={campaignData}
              characters={characters}
              characterName={characterName}
              characterId={user.uid}
              titleClass={classes.sectionTitle}
            />
          </Paper>

          {/* Character Sheet */}
          {character &&
            <Paper className={classes.paper} elevation={3} css={`display: flex; flex-direction: column;`}>
              <Character
                loading={charactersLoading}
                campaignId={campaignId}
                characterId={user.uid}
                character={character}
                titleClass={classes.sectionTitle} />
            </Paper>
          }

          {isGM &&
            <Paper className={classes.paper} elevation={3} css={`display: flex; justify-content: space-between; align-items: center;`}>
              <div>You are the GM.</div>
              <div css={`display: flex;`}>
                <NewSessionButton campaignId={campaignId} characterName={characterName} characters={characters} />
                <Button
                  css={`margin-left: 8px;`}
                  onClick={async () => await removeGM(campaignId)}>
                    Resign
                </Button>
              </div>
            </Paper>
          }

          {character &&
            <Paper className={classes.paper} elevation={3} css={`display: flex; flex-direction: column;`}>
              <Skills
                campaignId={campaignId}
                characterId={user.uid}
                details={character.details}
                careerList={character.careerList}
                setDicePool={onSetDicePool}
                skillRanks={character.skillRanks}
                titleClass={classes.sectionTitle} />
            </Paper>
          }

          <Paper className={classes.paper} elevation={3} css={`display: flex; flex-direction: column;`}>
            <Notepad isDarkMode={isDarkMode} titleClass={classes.sectionTitle} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
}

export default Campaign
